import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaRegHeart } from "react-icons/fa";
import { RiShoppingCartLine } from "react-icons/ri";
import { MdOutlinePlaylistAdd } from "react-icons/md";
import Rating from '../Rating';
import { useDispatch, useSelector } from 'react-redux';
import { add_to_card, add_to_wishlist, messageClear } from '../../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../../store/reducers/myListReducer';
import toast from 'react-hot-toast';
import { useCurrency } from '../../context/CurrencyContext';
import { openEnquiry } from '../../utils/enquiry';

const ShopProducts = ({ styles, products }) => {
    const { formatBase } = useCurrency();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.auth);
    const { errorMessage, successMessage } = useSelector(state => state.card);
    const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector(state => state.myList);

    const add_card = (id, product) => {
        if (product?.sellerType === 'bulk' || product?.enquiryOnly) {
            openEnquiry();
            return;
        }
        if (userInfo) {
            dispatch(add_to_card({
                userId: userInfo.id,
                quantity: 1,
                productId: id
            }));
        } else {
            navigate('/login');
        }
    };

    const add_wishlist = (pro) => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        dispatch(add_to_wishlist({
            userId: userInfo.id,
            productId: pro._id,
            name: pro.name,
            price: pro.price,
            image: pro.images[0],
            discount: pro.discount,
            rating: pro.rating,
            slug: pro.slug
        }));
    };

    const add_my_list = (pro) => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        dispatch(add_to_my_list({ userId: userInfo.id, productId: pro._id }));
    };

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(messageClear());
        }
        if (errorMessage) {
            toast.error(errorMessage);
            dispatch(messageClear());
        }
    }, [successMessage, errorMessage, dispatch]);

    useEffect(() => {
        if (myListSuccessMessage) {
            toast.success(myListSuccessMessage);
            dispatch(myListMessageClear());
        }
        if (myListErrorMessage) {
            toast.error(myListErrorMessage);
            dispatch(myListMessageClear());
        }
    }, [myListSuccessMessage, myListErrorMessage, dispatch]);

    const isEnquiryOnly = (product) => product?.sellerType === 'bulk' || product?.enquiryOnly;

    return (
        <div className={`w-full grid ${styles === 'grid' ? 'grid-cols-3 md-lg:grid-cols-2 md:grid-cols-2' : 'grid-cols-1 md-lg:grid-cols-2 md:grid-cols-2'} gap-3 `}>
            {
                products.map((p, i) => <div key={i} className={`flex transition-all duration-1000 hover:shadow-md hover:-translate-y-3 ${styles === 'grid' ? 'flex-col justify-start items-start' : 'justify-start items-center md-lg:flex-col md-lg:justify-start md-lg:items-start'} w-full gap-4 bg-white p-1 rounded-md`}>

                    <div className={styles === 'grid' ? 'w-full relative group h-[210px] md:h-[270px] xs:h-[170px] overflow-hidden' : 'md-lg:w-full relative group h-[210px] md:h-[270px] overflow-hidden'}>
                        <div className="w-full h-[240px] md:h-[270px] xs:h-[170px] flex justify-center items-center bg-white">
                            <img
                                className="max-h-full max-w-full object-contain rounded-md"
                                src={p.images[0]}
                                alt={p.name}
                            />
                        </div>


                        <ul className='flex transition-all duration-700 -bottom-10 justify-center items-center gap-2 absolute w-full group-hover:bottom-3'>
                            <li className='w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#059473] hover:text-white hover:rotate-[720deg] transition-all'>
                                <button onClick={() => add_wishlist(p)} aria-label="Add to wishlist">
                                    <FaRegHeart />
                                </button>
                            </li>
                            <li className='w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#059473] hover:text-white hover:rotate-[720deg] transition-all'>
                                <Link to={`/product/details/${p.slug}`}>
                                    <FaEye />
                                </Link>
                            </li>
                            <li className='w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#059473] hover:text-white hover:rotate-[720deg] transition-all'>
                                <button onClick={() => add_my_list(p)} aria-label="Add to My List" title="Add to My List">
                                    <MdOutlinePlaylistAdd size={18} />
                                </button>
                            </li>
                            {isEnquiryOnly(p) ? (
                                <li className='h-[38px] px-3 cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#059473] hover:text-white transition-all'>
                                    <button
                                        onClick={() => openEnquiry()}
                                        className="text-xs font-semibold"
                                        aria-label="Enquire now"
                                    >
                                        Enquire Now
                                    </button>
                                </li>
                            ) : (
                                <li className='w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#059473] hover:text-white hover:rotate-[720deg] transition-all'>
                                <button onClick={() => add_card(p._id, p)} aria-label="Add to cart">
                                    <RiShoppingCartLine />
                                </button>
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className='flex justify-start items-start flex-col gap-1'>
                        <h2 className='font-bold'>{p.name}</h2>
                        <div className='flex justify-start items-center gap-3'>
                            {p.sellerType === 'bulk' ? (
                                <span className='text-md font-semibold text-orange-600'>
                                    {p.bulkContainerPrice ? `From ${formatBase(p.bulkContainerPrice)}` : 'Enquiry Only'}
                                </span>
                            ) : p.sellerType === 'wholesale' && p.wholesalePrice ? (
                                <span className='text-md font-semibold text-blue-600'>{formatBase(p.wholesalePrice)}</span>
                            ) : (
                                <span className='text-md font-semibold'>{formatBase(p.price)}</span>
                            )}
                            <div className='flex'>
                                <Rating ratings={p.rating} />
                            </div>

                        </div>
                    </div>


                </div>

                )
            }

        </div>
    );
};

export default ShopProducts;
